/**
 * Widget Wrapper with Data Source Indicators
 * Wraps dashboard widgets with visual data source indicators and controls
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { DataSourceIndicator } from './DataSourceManager';
import {
  Cog6ToothIcon,
  ArrowsPointingOutIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';

const WidgetContainer = styled.div`
  position: relative;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary.main};
    
    .widget-controls {
      opacity: 1;
    }
  }
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: ${theme.colors.background.secondary};
  border-bottom: 1px solid ${theme.colors.border.secondary};
  min-height: 3rem;
`;

const WidgetTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const WidgetName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  margin: 0;
  flex: 1;
`;

const WidgetControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  &.widget-controls {
    opacity: 0;
  }
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

const DataSourceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
`;

const WidgetContent = styled.div`
  padding: 1rem;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StatusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${theme.fontSizes.sm};
  z-index: 10;
`;

const WidgetWrapper = ({ 
  widget, 
  children, 
  onEdit, 
  onDelete, 
  onResize,
  showControls = true,
  showDataSource = true,
  dataSourceType = 'database',
  lastUpdate = null,
  isOffline = false,
  isLoading = false
}) => {
  const [dataAge, setDataAge] = useState(0);

  // Update data age periodically
  useEffect(() => {
    if (!lastUpdate) return;
    
    const updateAge = () => {
      const now = new Date();
      const age = Math.floor((now - new Date(lastUpdate)) / 1000);
      setDataAge(age);
    };
    
    updateAge();
    const interval = setInterval(updateAge, 1000);
    
    return () => clearInterval(interval);
  }, [lastUpdate]);

  const getDataSourceType = () => {
    if (isOffline) return 'offline';
    if (widget?.dataSource === 'demo') return 'demo';
    if (widget?.dataMode === 'live') return 'real-time';
    if (dataAge < 30) return 'real-time';
    if (dataAge < 300) return 'near-real-time';
    return 'database';
  };

  const formatDataAge = (seconds) => {
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getStatusMessage = () => {
    if (isLoading) return 'Loading...';
    if (isOffline) return 'Data source offline';
    if (!lastUpdate) return 'No data received';
    if (dataAge > 300) return 'Data is inactive';
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <WidgetContainer>
      {showControls && (
        <WidgetHeader>
          <WidgetTitle>
            <WidgetName>{widget?.name || 'Unnamed Widget'}</WidgetName>
            {showDataSource && (
              <DataSourceBadge>
                <DataSourceIndicator 
                  sourceType={getDataSourceType()} 
                  size="xs" 
                  showTooltip={false}
                />
                {lastUpdate && formatDataAge(dataAge)}
              </DataSourceBadge>
            )}
          </WidgetTitle>
          
          <WidgetControls className="widget-controls">
            {onEdit && (
              <ControlButton onClick={() => onEdit(widget)} title="Edit Widget">
                <Cog6ToothIcon className="w-3 h-3" />
              </ControlButton>
            )}
            {onResize && (
              <ControlButton onClick={() => onResize(widget)} title="Resize Widget">
                <ArrowsPointingOutIcon className="w-3 h-3" />
              </ControlButton>
            )}
            <ControlButton title="More Options">
              <EllipsisVerticalIcon className="w-3 h-3" />
            </ControlButton>
          </WidgetControls>
        </WidgetHeader>
      )}
      
      <WidgetContent>
        {children}
      </WidgetContent>
      
      {statusMessage && (
        <StatusOverlay>
          <div>{statusMessage}</div>
          {isOffline && (
            <div style={{ fontSize: theme.fontSizes.xs, marginTop: '0.5rem' }}>
              Check your data source connection
            </div>
          )}
        </StatusOverlay>
      )}
    </WidgetContainer>
  );
};

export default WidgetWrapper;